import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '@/components/Ui-components/display-image';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import parse from 'html-react-parser';
import {fadeInUp} from '@/animation/scroll-animation';
import dynamic from 'next/dynamic';
import {borderRadius, colors, fontWeight, transition, zIndex} from '@/styles/tokens';

const AnimatedComponent = dynamic(() => import('../AnimatedComponent').then((mod) => mod.AnimatedComponent));

const quoteSx = {
    fontSize: {xs: '1rem', md: '1.1rem'},
    color: colors.text.primary,
    lineHeight: 1.6,
    mb: 3,
    fontFamily: "'RegularText', sans-serif"
} as const;

const nameSx = {
    fontWeight: fontWeight.bold,
    fontSize: '1.25rem',
    color: colors.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    fontFamily: "'Headers', sans-serif"
} as const;

const designationSx = {
    fontSize: '0.95rem',
    color: colors.text.secondary,
    fontFamily: "'RegularText', sans-serif"
} as const;

const imageWrapperSx = {position: 'relative', maxWidth: 352, mx: {xs: 'auto', md: '0'}, ml: {md: 'auto'}} as const;

const decorativeBoxSx = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 150,
    height: 150,
    bgcolor: colors.primary.decorative,
    zIndex: zIndex.base
} as const;

const imageForegroundSx = {position: 'relative', zIndex: zIndex.raised, pl: 4, pt: 4} as const;

const imageBoxSx = {width: '100%', maxWidth: 320, height: 320, overflow: 'hidden', borderRadius: borderRadius.sm} as const;

const contentBoxSx = {maxWidth: 800} as const;

const linkedInIconSx = {color: colors.brand.linkedin, cursor: 'pointer', fontSize: '1.75rem'} as const;

interface ExpertItem {
    name: string;
    quote: string;
    designation: string;
    image: any;
    linkedin?: string;
}

interface ExpertsProps {
    heading: string;
    expert?: ExpertItem;
    experts?: ExpertItem[];
}

const ExpertCard = ({expert}: {expert: ExpertItem}) => (
    <Grid container spacing={{xs: 4, md: 10}} alignItems="center" justifyContent="center">
        <Grid item xs={12} md={4}>
            <AnimatedComponent variants={fadeInUp}>
                <Box sx={imageWrapperSx}>
                    <Box sx={decorativeBoxSx} />
                    <Box sx={imageForegroundSx}>
                        <Box sx={imageBoxSx}>
                            <DisplayImage
                                src={expert.image}
                                alt={expert.name}
                                width={320}
                                height={320}
                                style={{width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top'}}
                            />
                        </Box>
                    </Box>
                </Box>
            </AnimatedComponent>
        </Grid>
        <Grid item xs={12} md={7}>
            <AnimatedComponent variants={fadeInUp} delay={0.2}>
                <Box sx={contentBoxSx}>
                    <Typography sx={quoteSx}>{expert.quote}</Typography>
                    <Box mt={4}>
                        <Typography sx={nameSx}>
                            {expert.name}
                            {expert.linkedin && (
                                <LinkedInIcon sx={linkedInIconSx} onClick={() => globalThis.open(expert.linkedin, '_blank')} />
                            )}
                        </Typography>
                        <Typography sx={designationSx}>{expert.designation}</Typography>
                    </Box>
                </Box>
            </AnimatedComponent>
        </Grid>
    </Grid>
);

const Experts = ({heading, expert, experts}: ExpertsProps) => {
    const list: ExpertItem[] = experts ?? (expert ? [expert] : []);
    const [activeIdx, setActiveIdx] = useState(0);
    const showDots = list.length > 1;

    if (list.length === 0) return null;

    return (
        <Box>
            <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={8}>
                <Typography variant="h1" component="h2" fontSize={{xs: 35, sm: 48}} className="font-headers">
                    {heading ? parse(heading) : ''}
                </Typography>
            </Box>
            <MUIContainer maxWidth="lg">
                <ExpertCard expert={list[activeIdx]} />

                {showDots && (
                    <Box display="flex" justifyContent="center" gap={1} mt={5}>
                        {list.map((_, i) => (
                            <Box
                                key={i}
                                onClick={() => setActiveIdx(i)}
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: borderRadius.circle,
                                    bgcolor: i === activeIdx ? colors.primary.deep : colors.neutral.grayMedium,
                                    cursor: 'pointer',
                                    transition: transition.base
                                }}
                            />
                        ))}
                    </Box>
                )}
            </MUIContainer>
        </Box>
    );
};

export default memo(Experts);
